import { useCallback, useEffect, useRef, useState } from 'react';
import { Column } from './components/Column';
import { Palette } from './components/Palette';
import { fetchMeta } from './lib/api';
import { decodeRefs, encodeRefs, randomChapter, sameChapter, step } from './lib/refs';
import type { Column as ColumnState, Meta, Ref } from './lib/types';

type PaletteState =
  | null
  | { kind: 'replace'; columnId: string }
  | { kind: 'add'; afterId: string };

let nextId = 0;
const makeColumn = (ref: Ref): ColumnState => ({ id: `col-${nextId++}`, ref });

const readUrl = () => new URLSearchParams(location.search).get('c');

/** Columns move together while they're all showing the same chapter. */
const isLinked = (columns: ColumnState[]) =>
  columns.length > 1 && columns.every((c) => sameChapter(c.ref, columns[0].ref));

export default function App() {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [columns, setColumns] = useState<ColumnState[]>([]);
  const [palette, setPalette] = useState<PaletteState>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);

  const bodies = useRef(new Map<string, HTMLDivElement>());
  const syncing = useRef(false);
  const popping = useRef(false);
  const firstSync = useRef(true);
  const booted = useRef(false);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;

    fetchMeta()
      .then((loaded) => {
        setMeta(loaded);
        const fromUrl = decodeRefs(readUrl(), loaded.books, loaded.translations);
        const initial = fromUrl.length > 0
          ? fromUrl
          : [{ translation: loaded.translations[0].id, ...randomChapter(loaded.books) }];
        setColumns(initial.map(makeColumn));
      })
      .catch((e: Error) => setFatal(e.message));
  }, []);

  // Keep the URL in step with the columns. Chapter changes become history
  // entries so the back button walks back through what you were reading;
  // the initial load and popstate restores only normalise the existing entry.
  useEffect(() => {
    if (!meta || columns.length === 0) return;

    // Consume both flags unconditionally — an early return that leaves one set
    // would silently suppress the next genuine URL update.
    const restoring = popping.current;
    const initial = firstSync.current;
    popping.current = false;
    firstSync.current = false;

    const query = `?c=${encodeRefs(columns.map((c) => c.ref), meta.books)}`;
    if (query === location.search) return;

    if (restoring || initial) history.replaceState(null, '', query);
    else history.pushState(null, '', query);
  }, [columns, meta]);

  useEffect(() => {
    if (!meta) return;
    const onPop = () => {
      const refs = decodeRefs(readUrl(), meta.books, meta.translations);
      if (refs.length === 0) return;
      popping.current = true;
      setColumns(refs.map(makeColumn));
    };
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, [meta]);

  const focused = columns.find((c) => c.id === focusedId) ?? columns[0];

  /**
   * Chapter movement: prev/next/random. When every column is on the same
   * chapter they travel together, each in its own translation.
   */
  const navigate = useCallback((columnId: string, make: (ref: Ref) => Ref) => {
    setColumns((current) => {
      const target = current.find((c) => c.id === columnId);
      if (!target) return current;

      const next = make(target.ref);
      if (!isLinked(current)) {
        return current.map((c) => (c.id === columnId ? { ...c, ref: next } : c));
      }
      return current.map((c) => ({
        ...c,
        ref: { translation: c.ref.translation, book: next.book, chapter: next.chapter },
      }));
    });
  }, []);

  const openPalette = useCallback((state: PaletteState) => setPalette(state), []);

  function onPaletteSelect(ref: Ref) {
    if (!palette) return;
    if (palette.kind === 'replace') {
      // An explicit jump targets exactly one column. Sending it to a different
      // chapter is how a reader breaks a parallel apart; picking a different
      // translation for the same chapter leaves the group linked.
      const { columnId } = palette;
      setColumns((current) =>
        current.map((c) => (c.id === columnId ? { ...c, ref } : c))
      );
    } else {
      const column = makeColumn(ref);
      setColumns((current) => {
        const at = current.findIndex((c) => c.id === palette.afterId);
        const next = [...current];
        next.splice(at + 1, 0, column);
        return next;
      });
      setFocusedId(column.id);
    }
    setPalette(null);
  }

  // Global shortcuts. Chapter arrows are ignored while typing or in the palette.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const typing = event.target instanceof HTMLElement &&
        (event.target.tagName === 'INPUT' || event.target.isContentEditable);

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        if (palette) setPalette(null);
        else if (focused) openPalette({ kind: 'replace', columnId: focused.id });
        return;
      }
      if (palette || typing || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'ArrowLeft' && focused) {
        navigate(focused.id, (ref) => step(ref, meta!.books, -1));
      } else if (event.key === 'ArrowRight' && focused) {
        navigate(focused.id, (ref) => step(ref, meta!.books, 1));
      }
    }
    addEventListener('keydown', onKeyDown);
    return () => removeEventListener('keydown', onKeyDown);
  }, [focused, palette, meta, navigate, openPalette]);

  const linked = isLinked(columns);

  /** Mirrors scroll position proportionally across linked columns. */
  const onScroll = useCallback((source: HTMLDivElement) => {
    if (!linked || syncing.current) return;
    syncing.current = true;

    const range = source.scrollHeight - source.clientHeight;
    const progress = range > 0 ? source.scrollTop / range : 0;

    for (const body of bodies.current.values()) {
      if (body === source) continue;
      const otherRange = body.scrollHeight - body.clientHeight;
      if (otherRange > 0) body.scrollTop = progress * otherRange;
    }
    requestAnimationFrame(() => (syncing.current = false));
  }, [linked]);

  if (fatal) {
    return (
      <div className='fatal'>
        <p>Couldn’t reach the chapter service.</p>
        <p className='fatal__detail'>{fatal}</p>
      </div>
    );
  }

  if (!meta || columns.length === 0) return <div className='booting' />;

  return (
    <>
      <main className='app'>
        {columns.map((column) => (
          <Column
            key={column.id}
            refValue={column.ref}
            books={meta.books}
            canClose={columns.length > 1}
            onFocus={() => setFocusedId(column.id)}
            onOpenPalette={() => openPalette({ kind: 'replace', columnId: column.id })}
            onAdd={() => openPalette({ kind: 'add', afterId: column.id })}
            onClose={() => setColumns((c) => c.filter((x) => x.id !== column.id))}
            onStep={(delta) => navigate(column.id, (ref) => step(ref, meta.books, delta))}
            onRandom={() =>
              navigate(column.id, (ref) => ({
                translation: ref.translation,
                ...randomChapter(meta.books),
              }))}
            scrollRef={(el) => {
              if (el) bodies.current.set(column.id, el);
              else bodies.current.delete(column.id);
            }}
            onScroll={onScroll}
          />
        ))}
      </main>

      {palette && focused && (
        <Palette
          meta={meta}
          context={columns.find((c) =>
            c.id === (palette.kind === 'replace' ? palette.columnId : palette.afterId)
          )?.ref ?? focused.ref}
          heading={palette.kind === 'add' ? 'Add a column…' : 'Go to chapter…'}
          onSelect={onPaletteSelect}
          onClose={() => setPalette(null)}
        />
      )}
    </>
  );
}
